/**
 * filterState — чистая логика состояния реляционного select-фильтра
 * (createRepositoryFilter), вынесенная из компонента для тестируемости.
 *
 * Кодирует два контракта, которые легко молча сломать при расширении:
 * 1) сериализация состояния в строку URL и обратно;
 * 2) XOR между «Без X» (sentinel) и выбором конкретных id.
 *
 * Состояние намеренно держит `without`/`ids` раздельно (а не один список со
 * sentinel) — это упрощает применение фильтра и сериализацию; sentinel
 * появляется только на границе с UI (см. {@link toMultiValue}/{@link toSingleValue}).
 */

/** Зарезервированный токен «Без X» — в URL и как значение sentinel-опции в UI. */
export const WITHOUT_TOKEN = '__without__';

/** XOR-состояние фильтра: либо «без значения», либо набор выбранных id. */
export interface FilterState {
  without: boolean;
  ids: string[];
}

export const EMPTY_STATE: FilterState = { without: false, ids: [] };

/** Сериализация состояния в строку URL (или null — фильтр снят). */
export function serialize(state: FilterState, isMulti: boolean): string | null {
  if (state.without) return WITHOUT_TOKEN;
  if (state.ids.length === 0) return null;
  return isMulti ? JSON.stringify(state.ids) : state.ids[0];
}

/** Разбор строки URL обратно в состояние. */
export function parse(raw: string | null, isMulti: boolean): FilterState {
  if (raw == null || raw === '') return EMPTY_STATE;
  if (raw === WITHOUT_TOKEN) return { without: true, ids: [] };
  if (isMulti) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return { without: false, ids: parsed.map(String) };
    } catch {
      // не JSON — трактуем как одиночный id
    }
  }
  return { without: false, ids: [String(raw)] };
}

/**
 * XOR-переход для multi: новый набор значений из combobox → состояние.
 * Выбор sentinel «Без X» сбрасывает id; выбор любого id сбрасывает «Без X».
 */
export function applyMultiSelection(prev: FilterState, values: string[]): FilterState {
  // Только что добавили sentinel (его не было) → оставляем только «Без X».
  if (values.includes(WITHOUT_TOKEN) && !prev.without) {
    return { without: true, ids: [] };
  }
  // Иначе — берём только реальные id, sentinel убираем (снятие «Без X» или выбор id).
  return { without: false, ids: values.filter(v => v !== WITHOUT_TOKEN) };
}

/** XOR-переход для single: выбранное значение combobox → состояние. */
export function applySingleSelection(value: string | null): FilterState {
  if (value === WITHOUT_TOKEN) return { without: true, ids: [] };
  return { without: false, ids: value ? [value] : [] };
}

/** Состояние → значение multi-combobox (sentinel вместо id при «Без X»). */
export function toMultiValue(state: FilterState): string[] {
  return state.without ? [WITHOUT_TOKEN] : state.ids;
}

/** Состояние → значение single-combobox (sentinel вместо id при «Без X»). */
export function toSingleValue(state: FilterState): string | null {
  return state.without ? WITHOUT_TOKEN : (state.ids[0] ?? null);
}
